import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useAuth } from "@sal/authentication.react";
import { get } from "@sal/webrequest-web";
import { assertApiSuccess } from "code/apiError";
import BulkActionBar, { BulkActionItem } from "components/BulkActionBar";
import { useNotification } from "components/NotificationProvider";
import FilterDialog from "components/personnelAndFleet/common/FilterDialog";
import FilterFields, { FilterOption } from "components/personnelAndFleet/common/FilterFields";
import DeactivateDriverWarningDialog from "components/personnelAndFleet/personnel/DeactivateDriverWarningDialog";
import PersonnelFilter from "components/personnelAndFleet/personnel/PersonnelFilter";
import UpsertPersonnelModal from "components/personnelAndFleet/personnel/UpsertPersonnelModal";
import {
	PERSONNEL_STATUS_FILTER_OPTIONS,
	PERSONNEL_STATUS_LABELS,
	PersonnelStatus,
	PersonnelStatusFilterOption
} from "enums/PersonnelEnums";
import {
	bulkActivatePersonnelUsersApi,
	bulkDeactivatePersonnelUsersApi,
	useActivatePersonnelUser,
	useApprovePersonnelUser,
	useDeactivatePersonnelUser,
	useGetPersonnelUsers
} from "hooks/personnelUsers";
import { useBreakpoint } from "hooks/useBreakpoint";
import { usePersonnelQueryParams } from "hooks/usePersonnelQueryParams";
import { FleetVehicle } from "models/FleetVehicle";
import { PersonnelUser } from "models/PersonnelUser";
import { useEffect, useMemo, useState, useTransition } from "react";
import { mutate, useSWRConfig } from "swr";
import PersonnelCardList from "./PersonnelCardList";
import PersonnelDataTable from "./PersonnelDataTable";

export default function PersonnelTab() {
	const theme = useTheme();
	const auth = useAuth();
	const canWrite = auth.userHasPermission("Personnel", "Write");
	const { isMobile, isTablet } = useBreakpoint();
	const { personnelStatus, setPersonnelStatus, searchFilter, setSearchFilter, applyFilters } =
		usePersonnelQueryParams();
	const [modalMode, setModalMode] = useState<"add" | "edit" | "duplicate" | null>(null);
	const [modalUser, setModalUser] = useState<PersonnelUser | null>(null);
	const modalOpen = modalMode !== null;
	const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
	const [bulkLoading, setBulkLoading] = useState<"deactivate" | "activate" | null>(null);
	const { data: personnel, isDataLoading, dataError, mutateData } = useGetPersonnelUsers();
	const { deactivatePersonnelUser } = useDeactivatePersonnelUser();
	const { activatePersonnelUser } = useActivatePersonnelUser();
	const { approvePersonnelUser } = useApprovePersonnelUser();
	const { showNotification } = useNotification();
	const [, startTransition] = useTransition();
	const { cache } = useSWRConfig();
	const [deactivateConfirm, setDeactivateConfirm] = useState<
		| { mode: "single"; user: PersonnelUser; affectedVehicles: string[] }
		| { mode: "bulk"; ids: number[]; affectedVehicles: string[] }
		| null
	>(null);
	const [filterModalOpen, setFilterModalOpen] = useState(false);
	const [pendingFilters, setPendingFilters] = useState({
		status: personnelStatus as string,
		search: searchFilter
	});

	useEffect(() => {
		if (dataError) {
			showNotification({ message: "Unable to load personnel data", severity: "error" });
		}
	}, [dataError, showNotification]);

	const handleSearchFilterChange = (value: string) => {
		startTransition(() => setSearchFilter(value));
	};

	const filteredRows = useMemo(() => {
		let filtered = personnel;

		if (personnelStatus === PERSONNEL_STATUS_FILTER_OPTIONS.PENDING_APPROVAL) {
			filtered = filtered.filter((user) => !user.cvWhenApproved);
		} else if (personnelStatus === PersonnelStatus.ACTIVE) {
			filtered = filtered.filter((user) => !user.cvWhenApproved || user.active === true);
		} else if (personnelStatus !== PERSONNEL_STATUS_FILTER_OPTIONS.ALL_PERSONNEL) {
			// DEACTIVATED
			filtered = filtered.filter((user) => !!user.cvWhenApproved && user.active === false);
		}

		const trimmedSearch = searchFilter.trim();
		if (trimmedSearch) {
			const query = trimmedSearch.toLowerCase();
			const isFullNameQuery = query.includes(" ");
			filtered = filtered.filter((user) => {
				const first = user.firstName?.toLowerCase() ?? "";
				const last = user.lastName?.toLowerCase() ?? "";
				if (first.includes(query) || last.includes(query)) {
					return true;
				}
				if (user.email?.toLowerCase().includes(query)) {
					return true;
				}
				if (isFullNameQuery) {
					return `${first} ${last}`.includes(query) || `${last} ${first}`.includes(query);
				}
				return false;
			});
		}

		return filtered;
	}, [personnel, personnelStatus, searchFilter]);

	const handleAddNewPersonnel = () => {
		setModalMode("add");
	};

	const handleCloseModal = () => {
		setModalMode(null);
		setModalUser(null);
	};

	const handleEditUser = (user: PersonnelUser) => {
		setModalUser(user);
		setModalMode("edit");
	};

	const handleDuplicateUser = (user: PersonnelUser) => {
		setModalUser(user);
		setModalMode("duplicate");
	};

	const getAffectedVehicleRegistrations = async (userIds: number[]): Promise<string[]> => {
		const cached = cache.get("api/Fleet/List") as { data?: FleetVehicle[] } | undefined;
		const fleet: FleetVehicle[] =
			cached?.data ??
			assertApiSuccess<FleetVehicle[]>(
				await get("api/Fleet/List"),
				"Failed to load fleet vehicles."
			);
		return fleet
			.filter((v) => v.driverContactId != null && userIds.includes(v.driverContactId))
			.map((v) => v.registration);
	};

	const doDeactivateUser = async (user: PersonnelUser) => {
		const result = await deactivatePersonnelUser(user.id);
		if (result.success) {
			showNotification({ message: "User deactivated successfully", severity: "success" });
		} else {
			showNotification({ message: result.errorMessage, severity: "error" });
		}
	};

	const handleDeactivateUser = async (user: PersonnelUser) => {
		const affected = await getAffectedVehicleRegistrations([user.id]);
		if (affected.length > 0) {
			setDeactivateConfirm({ mode: "single", user, affectedVehicles: affected });
			return;
		}
		await doDeactivateUser(user);
	};

	const handleActivateUser = async (user: PersonnelUser) => {
		const result = await activatePersonnelUser(user.id);
		if (result.success) {
			showNotification({ message: "User activated successfully", severity: "success" });
		} else {
			showNotification({ message: result.errorMessage, severity: "error" });
		}
	};

	const handleApproveUser = async (user: PersonnelUser) => {
		const result = await approvePersonnelUser(user.id);
		if (result.success) {
			showNotification({ message: "User approved successfully", severity: "success" });
		} else {
			showNotification({ message: result.errorMessage, severity: "error" });
		}
	};

	const selectedUsers = useMemo(
		() => personnel.filter((u) => selectedRows.has(u.id)),
		[personnel, selectedRows]
	);

	const hasSelectedActive = selectedUsers.some((u) => u.active === true);
	const hasSelectedDeactivated = selectedUsers.some((u) => u.active !== true);

	const doBulkDeactivate = async (ids: number[]) => {
		setBulkLoading("deactivate");
		try {
			const { succeeded, failed } = await bulkDeactivatePersonnelUsersApi(ids);

			if (succeeded.length > 0) {
				if (failed.length === 0) {
					setSelectedRows(new Set());
				} else {
					setSelectedRows((prev) => {
						const next = new Set(prev);
						succeeded.forEach((id) => next.delete(id));
						return next;
					});
				}
				mutateData();
				mutate("api/Fleet/List");
			}

			if (failed.length === 0) {
				showNotification({
					message:
						succeeded.length === 1
							? "User deactivated successfully"
							: `${succeeded.length} users deactivated successfully`,
					severity: "success"
				});
			} else if (succeeded.length === 0) {
				showNotification({
					message: "Failed to deactivate users. Please try again.",
					severity: "error"
				});
			} else {
				showNotification({
					message: `${succeeded.length} user${
						succeeded.length !== 1 ? "s" : ""
					} deactivated. ${failed.length} failed, please try again.`,
					severity: "warning"
				});
			}
		} catch {
			showNotification({
				message: "An unexpected error occurred. Please try again.",
				severity: "error"
			});
		} finally {
			setBulkLoading(null);
		}
	};

	const handleBulkDeactivate = async () => {
		const ids = selectedUsers.filter((u) => u.active === true).map((u) => u.id);
		const affected = await getAffectedVehicleRegistrations(ids);
		if (affected.length > 0) {
			setDeactivateConfirm({ mode: "bulk", ids, affectedVehicles: affected });
			return;
		}
		await doBulkDeactivate(ids);
	};

	const handleBulkActivate = async () => {
		const ids = selectedUsers.filter((u) => u.active !== true).map((u) => u.id);
		setBulkLoading("activate");
		try {
			const { succeeded, failed } = await bulkActivatePersonnelUsersApi(ids);

			if (succeeded.length > 0) {
				if (failed.length === 0) {
					setSelectedRows(new Set());
				} else {
					setSelectedRows((prev) => {
						const next = new Set(prev);
						succeeded.forEach((id) => next.delete(id));
						return next;
					});
				}
				mutateData();
				mutate("api/Fleet/List");
			}

			if (failed.length === 0) {
				showNotification({
					message:
						succeeded.length === 1
							? "User activated successfully"
							: `${succeeded.length} users activated successfully`,
					severity: "success"
				});
			} else if (succeeded.length === 0) {
				showNotification({
					message: "Failed to activate users. Please try again.",
					severity: "error"
				});
			} else {
				showNotification({
					message: `${succeeded.length} user${
						succeeded.length !== 1 ? "s" : ""
					} activated. ${failed.length} failed, please try again.`,
					severity: "warning"
				});
			}
		} catch {
			showNotification({
				message: "An unexpected error occurred. Please try again.",
				severity: "error"
			});
		} finally {
			setBulkLoading(null);
		}
	};

	const personnelStatusOptions = useMemo<FilterOption[]>(
		() =>
			Object.values(PERSONNEL_STATUS_FILTER_OPTIONS).map((s) => ({
				value: s,
				label: PERSONNEL_STATUS_LABELS[s as PersonnelStatus] ?? s
			})),
		[]
	);

	const handleOpenFilterModal = () => {
		setPendingFilters({ status: personnelStatus, search: searchFilter });
		setFilterModalOpen(true);
	};

	const handleApplyFilters = () => {
		applyFilters(pendingFilters.status as PersonnelStatusFilterOption, pendingFilters.search);
		setFilterModalOpen(false);
	};

	const handleResetPending = () =>
		setPendingFilters({ status: PERSONNEL_STATUS_FILTER_OPTIONS.ACTIVE, search: "" });

	const handleResetFilters = () => applyFilters(PERSONNEL_STATUS_FILTER_OPTIONS.ACTIVE, "");

	const handleDeactivateConfirm = async () => {
		const confirm = deactivateConfirm;
		setDeactivateConfirm(null);
		if (!confirm) {
			return;
		}
		if (confirm.mode === "single") {
			await doDeactivateUser(confirm.user);
		} else {
			await doBulkDeactivate(confirm.ids);
		}
	};

	const bulkActions: BulkActionItem[] = canWrite
		? [
				...(hasSelectedActive
					? [
							{
								label: selectedRows.size === 1 ? "Deactivate user" : "Deactivate users",
								onClick: handleBulkDeactivate,
								color: "error" as const,
								loading: bulkLoading === "deactivate",
								disabled: bulkLoading !== null
							}
						]
					: []),
				...(hasSelectedDeactivated
					? [
							{
								label: selectedRows.size === 1 ? "Activate user" : "Activate users",
								onClick: handleBulkActivate,
								color: "primary" as const,
								loading: bulkLoading === "activate",
								disabled: bulkLoading !== null
							}
						]
					: [])
			]
		: [];

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
				position: "relative"
			}}
		>
			<Box
				sx={{
					flex: "0 0 auto",
					minHeight: 0,
					py: { xs: 2, md: 2.5 },
					px: { xs: 2, md: 3 },
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					backgroundColor: theme.palette.divider,
					borderTop: `1px solid ${theme.palette.grey[500]}`
				}}
			>
				<PersonnelFilter
					personnelStatus={personnelStatus}
					onPersonnelStatusChange={setPersonnelStatus}
					searchFilter={searchFilter}
					onSearchFilterChange={handleSearchFilterChange}
					onAddNew={handleAddNewPersonnel}
					onOpenFilterModal={handleOpenFilterModal}
					onReset={handleResetFilters}
					canWrite={canWrite}
				/>
			</Box>

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					backgroundColor: "background.paper"
				}}
			>
				{isDataLoading ? (
					<Box
						sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
					>
						<CircularProgress size={24} />
					</Box>
				) : dataError ? (
					<Box
						sx={{
							height: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							p: 2
						}}
					>
						<Typography color="error.main">Unable to load personnel data.</Typography>
					</Box>
				) : isMobile || isTablet ? (
					<PersonnelCardList
						rows={filteredRows}
						selectedRows={selectedRows}
						setSelectedRows={setSelectedRows}
						selectionDisabled={bulkLoading !== null}
						onEditUser={handleEditUser}
						onDuplicateUser={handleDuplicateUser}
						onDeactivateUser={handleDeactivateUser}
						onActivateUser={handleActivateUser}
						onApproveUser={handleApproveUser}
						canWrite={canWrite}
					/>
				) : (
					<PersonnelDataTable
						rows={filteredRows}
						selectedRows={selectedRows}
						setSelectedRows={setSelectedRows}
						selectionDisabled={bulkLoading !== null}
						onEditUser={handleEditUser}
						onDuplicateUser={handleDuplicateUser}
						onDeactivateUser={handleDeactivateUser}
						onActivateUser={handleActivateUser}
						onApproveUser={handleApproveUser}
						canWrite={canWrite}
					/>
				)}
			</Box>

			<BulkActionBar
				selectedCount={selectedRows.size}
				entityLabel="personnel"
				entityLabelPlural="personnel"
				onClear={() => setSelectedRows(new Set())}
				actions={bulkActions}
			/>

			<UpsertPersonnelModal
				open={modalOpen}
				onClose={handleCloseModal}
				user={modalUser}
				mode={modalMode ?? "add"}
			/>

			<DeactivateDriverWarningDialog
				open={deactivateConfirm !== null}
				affectedVehicles={deactivateConfirm?.affectedVehicles ?? []}
				isBulk={deactivateConfirm?.mode === "bulk"}
				onConfirm={handleDeactivateConfirm}
				onCancel={() => setDeactivateConfirm(null)}
			/>
			<FilterDialog
				open={filterModalOpen}
				onClose={() => setFilterModalOpen(false)}
				onReset={handleResetPending}
				onSubmit={handleApplyFilters}
			>
				<FilterFields
					statusValue={pendingFilters.status}
					onStatusChange={(value) => setPendingFilters((prev) => ({ ...prev, status: value }))}
					statusOptions={personnelStatusOptions}
					searchValue={pendingFilters.search}
					onSearchChange={(value) => setPendingFilters((prev) => ({ ...prev, search: value }))}
					searchPlaceholder="Search personnel"
				/>
			</FilterDialog>
		</Box>
	);
}
